import {
  type Modification,
  type ModificationGroupLimits,
  type ModificationsMap,
  type SplitPortionAction,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the active modifications after applying a split portion operation.
 *
 * @remarks
 * Handles the following scenarios:
 *
 * - No portions selected (no modifications from the same group are selected):
 *    Adds the ingredient with max aggregate quantity
 *
 * - Selected ingredient has max portions:
 *    Removes the ingredient from the selection.
 *
 * - Selected ingredient without max portions + other selected:
 *    Removes the selected ingredient and sets the other selected ingredient to max aggregate quantity.
 *
 * - Default split:
 *    Sets all ingredients in the same group to quantity of 1.
 */
export function getModificationsAfterSplit(params: Params): Modification[] {
  const {
    activeModifications,
    ingredientId,
    modificationGroupName,
    modificationLimits,
    allModificationsMap,
    action,
  } = params;

  const modification = allModificationsMap.get(ingredientId);

  // ─── Missing Data ────────────────────────────────────────────────────

  if (!modification) {
    return activeModifications;
  }

  // ───────────────────────────────────────────────────────────────────────────

  const getSameGroupModifications = getSameGroupModificationsFactory(modificationGroupName); // prettier-ignore
  const getOtherModifications = getOtherModificationsFactory(modificationGroupName); // prettier-ignore
  const findSelectedModification = findSelectedModificationFactory(ingredientId); // prettier-ignore
  const filterOtherSelectedModifications = filterOtherSelectedModificationsFactory(ingredientId); // prettier-ignore
  const mapToQuantityOne = mapToQuantityOneFactory(); // prettier-ignore

  const modificationGroupLimits = modificationLimits[modificationGroupName];
  const maxAggregateQuantity = modificationGroupLimits?.maxAggregateQuantity ?? 1; // prettier-ignore

  const sameGroupModifications = activeModifications.filter(getSameGroupModifications); // prettier-ignore
  const otherModifications = activeModifications.filter(getOtherModifications);

  const isCompleteRemoval = action === 'complete-removal';
  const hasNoPortionsSelected = sameGroupModifications.length === 0 && !isCompleteRemoval; // prettier-ignore

  // ─── No Portions Selected ──────────────────────────────────────────────────

  if (hasNoPortionsSelected) {
    return [...otherModifications, { ...modification, quantity: maxAggregateQuantity }];
  }

  // ─── Has Portions Selected ─────────────────────────────────────────────────

  const selectedModification = sameGroupModifications.find(findSelectedModification); // prettier-ignore

  const isSelectedModification = selectedModification !== undefined;
  const hasMaxPortions = selectedModification && selectedModification.quantity >= maxAggregateQuantity; // prettier-ignore
  const hasNotReachedMaxPortions = selectedModification && selectedModification.quantity < maxAggregateQuantity; // prettier-ignore
  const otherSelectedModifications = sameGroupModifications.filter(filterOtherSelectedModifications); // prettier-ignore
  const hasOtherSelectedIngredients = otherSelectedModifications.length > 0;

  const shouldRemoveSelectedModification = isSelectedModification && hasMaxPortions; // prettier-ignore
  const shouldAddMaxLeftPortions = isCompleteRemoval || (isSelectedModification && hasNotReachedMaxPortions && hasOtherSelectedIngredients); // prettier-ignore

  // ─── Remove Selected Modification ──────────────────────────────────────────

  if (shouldRemoveSelectedModification) {
    return [...otherModifications, ...otherSelectedModifications];
  }

  // ─── Add Max Left Portions To Other Selected Modification ──────────────────

  if (shouldAddMaxLeftPortions) {
    const mapToMaxQuantityForFirst = mapToMaxQuantityForFirstFactory(maxAggregateQuantity); // prettier-ignore

    const updatedOtherSelectedModifications = otherSelectedModifications.map(mapToMaxQuantityForFirst); // prettier-ignore

    return [...otherModifications, ...updatedOtherSelectedModifications];
  }

  // ─── Set Each Ingredient To 1 Portion ──────────────────────────────────────

  const updatedModifications = [...otherModifications, ...sameGroupModifications.map(mapToQuantityOne)]; // prettier-ignore

  if (isSelectedModification) {
    return updatedModifications;
  }

  return [...updatedModifications, { ...modification, quantity: 1 }];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSameGroupModificationsFactory(modificationGroupName: string) {
  return (modification: Modification) => modification.modificationGroupName === modificationGroupName; // prettier-ignore
}

function getOtherModificationsFactory(modificationGroupName: string) {
  return (modification: Modification) => modification.modificationGroupName !== modificationGroupName; // prettier-ignore
}

function findSelectedModificationFactory(
  ingredientId: string,
): (modification: Modification) => boolean {
  return (modification: Modification) => modification.ingredient?.id === ingredientId; // prettier-ignore
}

function filterOtherSelectedModificationsFactory(
  ingredientId: string,
): (modification: Modification) => boolean {
  return (modification: Modification) => modification.ingredient?.id !== ingredientId; // prettier-ignore
}

function mapToQuantityOneFactory(): (modification: Modification) => Modification {
  return (modification: Modification) => ({ ...modification, quantity: 1 });
}

function mapToMaxQuantityForFirstFactory(
  maxAggregateQuantity: number,
): (modification: Modification, index: number) => Modification {
  return (modification: Modification, index: number) => {
    const quantity = index === 0 ? maxAggregateQuantity : modification.quantity;

    return { ...modification, quantity };
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  ingredientId: string;
  modificationGroupName: string;
  activeModifications: Modification[];
  modificationLimits: ModificationGroupLimits;
  allModificationsMap: ModificationsMap;
  action: SplitPortionAction;
};
